// Le grand livre du stock sur l'appareil: port de backend/app/core/stock_ledger.py,
// backend/app/core/depot.py et des effets automatiques de
// backend/app/services/stock_automation.py. Mêmes règles, mêmes chiffres: les
// deux côtés sont vérifiés par les jeux de données de
// backend/app/tests/fixtures/offline_parity/.
import { nouvelIdentifiant } from '../paths';

export const SENS_PAR_TYPE = {
  IMPORT: 1,
  ADJUSTMENT: 1,
  SALE: -1,
  INTERNAL_TRANSFER: -1,
  INTERNAL_CONSUMPTION: -1,
  LOSS: -1,
  IN_TRANSIT: 0,
  // Livraison reçue dans une cuve de station (lot 031) : une entrée.
  DELIVERY: 1,
};

/** Emplacement d'une cuve de station : stock propre à la station, jamais celui du dépôt. */
export const CUVE_DE_STATION = 'STATION_TANK';

const SOURCES_DE_CREDIT = [
  'AUTO_TRIP_BORDER_CROSSING',
  'AUTO_TRIP_ARRIVAL',
  'AUTO_TRIP_COMPLETION',
  'AUTO_TRIP_DIRECT_IMPORT',
];
const VENTE_DIRECTE = 'AUTO_TRIP_DIRECT_DELIVERY';
const SOURCES_AUTOMATIQUES = new Set([...SOURCES_DE_CREDIT, VENTE_DIRECTE, 'AUTO_TRIP_LOADING']);
const SORTIES_LIVREES = new Set(['SALE', 'INTERNAL_TRANSFER']);
const AUTRES_SORTIES = new Set(['LOSS', 'INTERNAL_CONSUMPTION']);
const SOURCES_STATION = new Set(['STATION_POS']);
const STATUTS_LIVRES = new Set(['DELIVERED', 'COMPLETED']);

const SOURCES_SANS_DEPOT = new Set([
  'AUTO_TRIP_BORDER_CROSSING',
  'AUTO_TRIP_DIRECT_IMPORT',
  'AUTO_TRIP_LOADING',
  'STATION_POS',
]);

const texte = (valeur) => String(valeur ?? '');
const maj = (valeur) => texte(valeur).toUpperCase();
const num = (valeur) => {
  const n = Number.parseFloat(valeur);
  return Number.isFinite(n) ? n : 0;
};
const jour = (valeur) => texte(valeur).slice(0, 10);

export const quantite = (mouvement) => num(mouvement.quantity_liters);
export const quantiteSignee = (mouvement) =>
  quantite(mouvement) * (SENS_PAR_TYPE[texte(mouvement.movement_type)] ?? 0);
export const estTransit = (mouvement) => texte(mouvement.movement_type) === 'IN_TRANSIT';
export const estTransitOuvert = (mouvement) => estTransit(mouvement) && !mouvement.settled_at;
/** La ligne entre dans une cuve de station ou en sort (lot 031). */
export const estMouvementDeCuve = (mouvement) =>
  texte(mouvement.to_location) === CUVE_DE_STATION || texte(mouvement.from_location) === CUVE_DE_STATION;
/** Litres gagnés (+) ou perdus (−) par les cuves de station du fait de cette ligne. */
export const deltaDeCuve = (mouvement) => {
  const q = quantite(mouvement);
  return (texte(mouvement.to_location) === CUVE_DE_STATION ? q : 0) - (texte(mouvement.from_location) === CUVE_DE_STATION ? q : 0);
};

export function calculerSoldes(mouvements) {
  const soldes = {
    available_liters: 0,
    in_transit_liters: 0,
    imports: 0,
    sales: 0,
    transfers: 0,
    consumption: 0,
    losses: 0,
    adjustments: 0,
    total_value: 0,
    movement_count: 0,
    station_tank_liters: 0,
    station_receipts: 0,
    station_sales: 0,
    station_adjustments: 0,
    station_movement_count: 0,
  };
  for (const mouvement of mouvements) {
    const q = quantite(mouvement);
    const type = texte(mouvement.movement_type);
    // Les cuves de station ont leur propre stock (lot 031) : hors des totaux du dépôt.
    if (estMouvementDeCuve(mouvement)) {
      const delta = deltaDeCuve(mouvement);
      soldes.station_movement_count += 1;
      soldes.station_tank_liters += delta;
      if (type === 'SALE') soldes.station_sales += q;
      // Inverse d'une clôture de poste rouverte (lot 030) : ventes nettes, pas un ajustement.
      else if (texte(mouvement.source) === 'STATION_SHIFT_REOPEN') soldes.station_sales -= delta;
      else if ((type === 'DELIVERY' || type === 'IMPORT') && delta > 0) soldes.station_receipts += q;
      else soldes.station_adjustments += delta;
      continue;
    }
    soldes.movement_count += 1;
    soldes.available_liters += quantiteSignee(mouvement);
    soldes.total_value += num(mouvement.total_value);
    if (type === 'IMPORT') soldes.imports += q;
    else if (type === 'SALE') soldes.sales += q;
    else if (type === 'INTERNAL_TRANSFER') soldes.transfers += q;
    else if (type === 'INTERNAL_CONSUMPTION') soldes.consumption += q;
    else if (type === 'LOSS') soldes.losses += q;
    else if (type === 'ADJUSTMENT') soldes.adjustments += q;
    if (estTransitOuvert(mouvement)) soldes.in_transit_liters += q;
  }
  return soldes;
}

/** Le stock d'une société sans dépôt: chargé, en route, pas encore livré. */
export function chargeNonLivre(
  mouvements,
  { trajetsAnnules = [], livraisonsParTrajet = {}, auJour = null, statutsDesTrajets = {} } = {},
) {
  const statuts = Object.fromEntries(Object.entries(statutsDesTrajets).map(([k, v]) => [String(k), maj(v)]));
  const livreLe = {};
  const derniereSortie = {};
  const annules = new Set((trajetsAnnules || []).filter(Boolean).map(String));
  const parTrajet = {};
  let sansTrajet = 0;
  for (const mouvement of mouvements) {
    const tripId = mouvement.trip_id || livraisonsParTrajet[texte(mouvement.delivery_id)];
    if (tripId && annules.has(String(tripId))) continue;
    if (SOURCES_STATION.has(texte(mouvement.source))) continue;
    if (estMouvementDeCuve(mouvement)) continue;
    const jourMouvement = jour(mouvement.movement_date || mouvement.created_at);
    if (tripId && !estTransit(mouvement) && quantiteSignee(mouvement) < 0) {
      const cle = String(tripId);
      const candidat = jourMouvement || '0000-00-00';
      derniereSortie[cle] = (derniereSortie[cle] || '') > candidat ? derniereSortie[cle] : candidat;
    }
    if (auJour && jourMouvement && jourMouvement > auJour) continue;
    if (tripId && texte(mouvement.source) === VENTE_DIRECTE) {
      const statut = statuts[String(tripId)];
      if (statut && !STATUTS_LIVRES.has(statut)) continue;
      livreLe[String(tripId)] = jourMouvement || '0000-00-00';
    }
    const q = quantite(mouvement);
    let delta;
    if (estTransit(mouvement)) {
      const solde = jour(mouvement.settled_at);
      const ouvert = !solde || (auJour !== null && solde > auJour);
      delta = ouvert ? q : 0;
    } else {
      delta = quantiteSignee(mouvement);
    }
    if (!delta) continue;
    if (tripId) parTrajet[String(tripId)] = (parTrajet[String(tripId)] || 0) + delta;
    else sansTrajet += delta;
  }
  Object.keys(parTrajet).forEach((trajet) => {
    const livreMaintenant = STATUTS_LIVRES.has(statuts[trajet]);
    if (
      trajet in livreLe ||
      (auJour === null && livreMaintenant) ||
      (livreMaintenant && trajet in derniereSortie && derniereSortie[trajet] <= texte(auJour))
    ) {
      delete parTrajet[trajet];
    }
  });
  const arrondi = (v) => Math.round(v * 100) / 100;
  const retenus = {};
  Object.entries(parTrajet).forEach(([trajet, litres]) => {
    if (litres >= 0.5) retenus[trajet] = arrondi(litres);
  });
  const total = Math.max(
    Object.values(retenus).reduce((s, v) => s + v, 0) + Math.min(sansTrajet, 0),
    0,
  );
  return { liters: total, by_trip: retenus, unassigned_liters: sansTrajet };
}

// --- Dépôt -------------------------------------------------------------------------------

export function preuveDeDepot(mouvement) {
  if (!['IMPORT', 'ADJUSTMENT'].includes(maj(mouvement.movement_type))) return false;
  if (SOURCES_SANS_DEPOT.has(maj(mouvement.source))) return false;
  if (['STATION', 'STATION_TANK', 'INTERNAL_TANK'].includes(maj(mouvement.to_location))) return false;
  return true;
}

export const deduireGestionDepot = (mouvements) => mouvements.some(preuveDeDepot);

export const gestionDepotEffective = (reglage, mouvements) =>
  reglage === null || reglage === undefined ? deduireGestionDepot(mouvements) : Boolean(reglage);

// --- Résumé de la page « Stock » (api/v1/stock.py: get_stock_summary) ---------------------------

export const LIBELLE_STOCK_CHARGE = 'Carburant chargé, non livré';

/** Résumé d'une station : ses cuves seulement (api/v1/stock.py: station_summary). */
export function construireResumeStation(mouvements, station) {
  const soldes = calculerSoldes(mouvements);
  const parCuve = {};
  mouvements.forEach((m) => {
    if (texte(m.to_location) === CUVE_DE_STATION) parCuve[m.to_location_id] = (parCuve[m.to_location_id] || 0) + quantite(m);
    if (texte(m.from_location) === CUVE_DE_STATION) parCuve[m.from_location_id] = (parCuve[m.from_location_id] || 0) - quantite(m);
  });
  const quotidien = {};
  [...mouvements]
    .sort((a, b) => texte(a.movement_date).localeCompare(texte(b.movement_date)))
    .forEach((m) => {
      const d = jour(m.movement_date);
      if (d) quotidien[d] = (quotidien[d] || 0) + deltaDeCuve(m);
    });
  let cumul = 0;
  const tendance = Object.keys(quotidien).sort().map((date) => {
    cumul += quotidien[date];
    return { date, balance: cumul };
  });
  const stock = soldes.station_tank_liters;
  return {
    scope: 'station',
    station_id: station,
    manages_depot: true,
    stock_label: 'Stock des cuves',
    current_stock_liters: stock,
    imports: soldes.station_receipts,
    in_transit_liters: 0,
    available_stock_liters: stock,
    sales: soldes.station_sales,
    transfers: 0,
    internal_consumption: 0,
    losses: 0,
    adjustments: soldes.station_adjustments,
    movement_count: soldes.station_movement_count,
    by_station: [
      {
        station_id: station,
        tank_liters: stock,
        by_tank: Object.keys(parCuve)
          .sort()
          .map((tankId) => ({ tank_id: tankId, liters: Math.round(parCuve[tankId] * 100) / 100 })),
      },
    ],
    by_truck: [],
    trend: tendance,
  };
}

/** Ligne d'une cuve de cette station : par la station, ou par une cuve connue de la station. */
export function lignesDeLaStation(mouvements, station) {
  const cuves = new Set(
    mouvements
      .filter((m) => m.station_id === station && estMouvementDeCuve(m))
      .flatMap((m) => [m.to_location_id, m.from_location_id])
      .filter(Boolean),
  );
  return mouvements.filter(
    (m) => estMouvementDeCuve(m) && (m.station_id === station || cuves.has(m.to_location_id) || cuves.has(m.from_location_id)),
  );
}

export function construireResumeStock(mouvements, { managesDepot, trajets = [], commandes = [], filtre = null } = {}) {
  const utiles = filtre ? mouvements.filter(filtre) : mouvements;
  const soldes = calculerSoldes(utiles);
  const parStation = {};
  for (const m of utiles) {
    if (m.to_location === 'STATION' && m.to_location_id) {
      const id = m.to_location_id;
      parStation[id] = parStation[id] || { station_id: id, delivered_liters: 0 };
      parStation[id].delivered_liters += num(m.quantity_liters);
    }
  }
  let parCamion = {};
  for (const m of utiles) {
    if (!m.trip_id) continue;
    if (m.movement_type === 'IMPORT') {
      const e = (parCamion[m.trip_id] = parCamion[m.trip_id] || { trip_id: m.trip_id, imported_liters: 0, in_transit_liters: 0 });
      e.imported_liters += num(m.quantity_liters);
    } else if (estTransitOuvert(m)) {
      const e = (parCamion[m.trip_id] = parCamion[m.trip_id] || { trip_id: m.trip_id, imported_liters: 0, in_transit_liters: 0 });
      e.in_transit_liters += num(m.quantity_liters);
    }
  }
  const tries = [...utiles].sort((a, b) => texte(a.movement_date).localeCompare(texte(b.movement_date)));
  const quotidien = {};
  for (const m of tries) {
    const d = jour(m.movement_date);
    // Les cuves de station ont leur propre stock : hors de la courbe du dépôt.
    if (!d || estMouvementDeCuve(m)) continue;
    quotidien[d] = (quotidien[d] || 0) + quantiteSignee(m);
  }
  let cumul = 0;
  const tendance = Object.keys(quotidien).sort().map((date) => {
    cumul += quotidien[date];
    return { date, balance: cumul };
  });

  let courant = soldes.available_liters;
  let etiquette = 'Stock disponible';
  if (!managesDepot) {
    const parTrajetId = Object.fromEntries(trajets.map((t) => [String(t.id), t]));
    const livraisons = {};
    commandes.forEach((c) =>
      (c.delivery_items || []).forEach((item) => {
        if (item && item.id && item.trip_id) livraisons[String(item.id)] = String(item.trip_id);
      }),
    );
    const charge = chargeNonLivre(utiles, {
      trajetsAnnules: trajets.filter((t) => maj(t.status) === 'CANCELLED').map((t) => t.id),
      livraisonsParTrajet: livraisons,
      statutsDesTrajets: Object.fromEntries(trajets.map((t) => [t.id, t.status])),
    });
    courant = Math.round(charge.liters * 100) / 100;
    etiquette = LIBELLE_STOCK_CHARGE;
    parCamion = Object.fromEntries(
      Object.entries(charge.by_trip)
        .sort((a, b) => b[1] - a[1])
        .map(([tripId, litres]) => [
          tripId,
          {
            trip_id: tripId,
            truck_id: (parTrajetId[tripId] || {}).truck_id ?? null,
            imported_liters: 0,
            in_transit_liters: litres,
          },
        ]),
    );
  }
  return {
    manages_depot: Boolean(managesDepot),
    stock_label: etiquette,
    station_tank_liters: soldes.station_tank_liters,
    current_stock_liters: courant,
    imports: soldes.imports,
    in_transit_liters: soldes.in_transit_liters,
    available_stock_liters: courant,
    sales: soldes.sales,
    transfers: soldes.transfers,
    internal_consumption: soldes.consumption,
    losses: soldes.losses,
    adjustments: soldes.adjustments,
    by_station: Object.values(parStation),
    by_truck: Object.values(parCamion),
    trend: tendance,
  };
}

// --- Effets automatiques d'un trajet sur le stock ------------------------------------------------

export function volumeChargeDuTrajet(trajet) {
  const reel = num(trajet.fuel_quantity_real);
  if (reel > 0) return { quantity: reel, estimated: false };
  const estime = num(trajet.allocated_quantity) || num(trajet.fuel_quantity_estimated);
  return { quantity: estime || 0, estimated: true };
}

const mouvementAuto = (trajet, champs) => {
  const maintenant = new Date().toISOString();
  return {
    id: nouvelIdentifiant(),
    unit_cost: trajet.fuel_price_unit ?? null,
    from_location_id: null,
    to_location_id: null,
    trip_id: trajet.id,
    order_id: null,
    delivery_id: null,
    created_by: 'SYSTEM',
    organization_id: trajet.organization_id ?? null,
    created_at: maintenant,
    updated_at: maintenant,
    movement_date: maintenant,
    ...champs,
    total_value: num(champs.quantity_liters) * num(trajet.fuel_price_unit),
  };
};

/**
 * Applique à une liste de mouvements ce que fait le serveur au changement de
 * statut d'un trajet. Rend `{ ecritures: [mouvement…], suppressions: [id…] }`.
 */
export function effetsDuStatutSurLeStock({ trajet, ancienStatut, nouveauStatut, mouvements, managesDepot, tousLesMouvementsDuTrajet = null }) {
  const doTrajet = tousLesMouvementsDuTrajet || mouvements.filter((m) => m.trip_id === trajet.id);
  const ecritures = [];
  const suppressions = [];
  const parSource = (source) => doTrajet.find((m) => m.source === source);
  const ecrire = (mouvement) => {
    const index = ecritures.findIndex((e) => e.id === mouvement.id);
    if (index >= 0) ecritures[index] = mouvement;
    else ecritures.push(mouvement);
    const local = doTrajet.findIndex((m) => m.id === mouvement.id);
    if (local >= 0) doTrajet[local] = mouvement;
    else doTrajet.push(mouvement);
  };
  const solderTransit = (jusquA) => {
    const chargement = parSource('AUTO_TRIP_LOADING');
    if (chargement && !chargement.settled_at) ecrire({ ...chargement, settled_at: jusquA, updated_at: new Date().toISOString() });
  };
  const credit = () => SOURCES_DE_CREDIT.map(parSource).find(Boolean) || null;

  const quitteLeChargement = ancienStatut === 'LOADING' && !['LOADING', 'CANCELLED'].includes(nouveauStatut);
  if (quitteLeChargement) {
    const volume = volumeChargeDuTrajet(trajet);
    const existant = parSource('AUTO_TRIP_LOADING');
    if (existant) {
      ecrire({
        ...existant,
        quantity_liters: volume.quantity,
        unit_cost: trajet.fuel_price_unit ?? null,
        total_value: volume.quantity * num(trajet.fuel_price_unit),
        updated_at: new Date().toISOString(),
      });
    } else {
      ecrire(
        mouvementAuto(trajet, {
          movement_type: 'IN_TRANSIT',
          quantity_liters: volume.quantity,
          from_location: 'IN_TRANSIT',
          to_location: 'IN_TRANSIT',
          source: 'AUTO_TRIP_LOADING',
          notes: volume.estimated
            ? 'Carburant chargé en transit, non disponible (volume estimé)'
            : 'Carburant chargé en transit, non disponible',
        }),
      );
    }
  }

  if (nouveauStatut === 'ARRIVED_AT_DEPOT' && ancienStatut !== 'ARRIVED_AT_DEPOT') {
    const existant = [...SOURCES_DE_CREDIT, VENTE_DIRECTE].map(parSource).find(Boolean);
    const q = num(trajet.fuel_quantity_real) || num(trajet.allocated_quantity) || num(trajet.fuel_quantity_estimated);
    if (!existant) {
      const date = trajet.return_date || new Date().toISOString();
      const entree = mouvementAuto(trajet, {
        movement_type: 'IMPORT',
        quantity_liters: q,
        from_location: 'IN_TRANSIT',
        to_location: 'DEPOT',
        source: 'AUTO_TRIP_ARRIVAL',
        movement_date: date,
        notes: 'Réception validée: le carburant devient disponible',
      });
      ecrire(entree);
      solderTransit(date);
    }
  }

  if (nouveauStatut === 'CLEARED_CUSTOMS_ENTRY' && ancienStatut !== 'CLEARED_CUSTOMS_ENTRY') {
    if (!parSource('AUTO_TRIP_BORDER_CROSSING')) {
      const ouverts = doTrajet.filter((m) => m.source === 'AUTO_TRIP_LOADING' && !m.settled_at);
      const q = ouverts.reduce((s, m) => s + num(m.quantity_liters), 0);
      if (q > 0) {
        const maintenant = new Date().toISOString();
        ecrire(
          mouvementAuto(trajet, {
            movement_type: 'IMPORT',
            quantity_liters: q,
            from_location: 'IN_TRANSIT',
            to_location: 'DEPOT',
            source: 'AUTO_TRIP_BORDER_CROSSING',
            movement_date: maintenant,
            notes: "Douane d'entrée franchie: le carburant devient disponible",
          }),
        );
        solderTransit(maintenant);
      }
    }
  }

  let ventePrevue =
    nouveauStatut === 'EN_ROUTE_TO_DELIVERY' && ancienStatut === 'CLEARED_CUSTOMS_ENTRY';
  if (ventePrevue && !managesDepot) ventePrevue = false;
  if (!ventePrevue && nouveauStatut === 'DELIVERED' && ancienStatut !== 'DELIVERED' && !managesDepot) {
    ventePrevue = !parSource('AUTO_TRIP_ARRIVAL');
  }
  if (ventePrevue) {
    const chargeInitial =
      num(trajet.fuel_quantity_real) || num(trajet.allocated_quantity) || num(trajet.fuel_quantity_estimated);
    const dateMouvement = trajet.return_date || new Date().toISOString();
    let entree = credit();
    let charge = chargeInitial;
    if (!entree) {
      entree = mouvementAuto(trajet, {
        movement_type: 'IMPORT',
        quantity_liters: chargeInitial,
        from_location: 'IN_TRANSIT',
        to_location: 'DEPOT',
        source: 'AUTO_TRIP_DIRECT_IMPORT',
        movement_date: dateMouvement,
        notes: 'Livraison directe: entrée comptable avant sortie immédiate',
      });
      ecrire(entree);
    } else {
      charge = num(entree.quantity_liters) || chargeInitial;
    }
    reequilibrerVenteDirecte({ trajet, doTrajet, charge, ecrire, suppressions, creer: true, date: dateMouvement });
    solderTransit(dateMouvement);
  } else if (nouveauStatut === 'DELIVERED' && ancienStatut !== 'DELIVERED') {
    const charge = num((credit() || {}).quantity_liters) || num(trajet.fuel_quantity_real) || num(trajet.allocated_quantity) || num(trajet.fuel_quantity_estimated);
    reequilibrerVenteDirecte({ trajet, doTrajet, charge, ecrire, suppressions, creer: false });
  }
  return { ecritures, suppressions };
}

/** Ce que les livraisons enregistrées ont déjà sorti du stock pour ce trajet. */
export function sortiesEnregistrees(doTrajet) {
  const totaux = { delivered: 0, other: 0 };
  for (const ligne of doTrajet) {
    const source = texte(ligne.source);
    if (SOURCES_AUTOMATIQUES.has(source) || source === 'STATION_POS') continue;
    if (maj(ligne.from_location) === 'STATION_TANK') continue;
    const type = maj(ligne.movement_type);
    const q = num(ligne.quantity_liters);
    if (SORTIES_LIVREES.has(type)) totaux.delivered += q;
    else if (AUTRES_SORTIES.has(type)) totaux.other += q;
  }
  return totaux;
}

export function reequilibrerVenteDirecte({ trajet, doTrajet, charge, ecrire, suppressions, creer, date = null }) {
  if (maj(trajet.status) === 'CANCELLED') return;
  const existante = doTrajet.find((m) => m.source === VENTE_DIRECTE);
  if (!existante && !creer) return;
  const enregistre = sortiesEnregistrees(doTrajet);
  const plafond = Math.max(charge - enregistre.other, 0);
  const cible = Math.round(Math.max(plafond - enregistre.delivered, 0) * 100) / 100;
  const maintenant = new Date().toISOString();
  if (existante) {
    if (cible <= 0) {
      suppressions.push(existante.id);
      const index = doTrajet.findIndex((m) => m.id === existante.id);
      if (index >= 0) doTrajet.splice(index, 1);
    } else if (Math.abs(num(existante.quantity_liters) - cible) > 0.005) {
      ecrire({
        ...existante,
        quantity_liters: cible,
        unit_cost: trajet.fuel_price_unit ?? null,
        total_value: cible * num(trajet.fuel_price_unit),
        updated_at: maintenant,
      });
    }
    return;
  }
  if (cible <= 0) return;
  ecrire(
    mouvementAuto(trajet, {
      movement_type: 'SALE',
      quantity_liters: cible,
      from_location: 'DEPOT',
      to_location: 'CLIENT',
      to_location_id: trajet.order_id ?? null,
      source: VENTE_DIRECTE,
      movement_date: date || trajet.return_date || maintenant,
      notes: 'Livraison directe au client, sans passage au dépôt',
    }),
  );
}
